import React from 'react';
import { connect } from 'react-redux';

import { toggleModal } from 'apps/auth/actions/appAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { InfoPage } from 'apps/auth/components/common/Layout/InfoPage/InfoPage';
import { Row } from 'apps/auth/components/common/Layout/Row';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper } from 'apps/auth/components/common/Panel/styles';
import { Text } from 'apps/auth/components/common/Text/Text';
import ModalComponent from 'apps/auth/components/utils/ModalComponent';
import { getLocalizedRawAmount } from 'apps/auth/helpers/formats';
import { resolveArguments, toLowerCase } from 'apps/auth/helpers/utils';
import { useApp, useExtra, useLanguage, usePageMessages } from 'apps/auth/hooks/selectors';

import { LimitBlock } from '../gaLimits/LimitBlock';

const GaLimits = (props) => {
    const { country } = useApp();
    const { lang } = useLanguage();
    const {
        alarm_limits,
        kycCheckInProcess,
        aml_libre_increase_level_link,
        libre_topup_limit_6_months,
        libre_atm_limit_daily,
        isSuccessKycConfirmation,
        libreCurrentLimitLevel,
    } = useExtra();

    const messages = usePageMessages();

    const isFirstLvl = libreCurrentLimitLevel === 1;

    const isCurrLessMaxValue = (counter) => {
        return counter.current_value < counter.max_value;
    };

    const limitProcessButton = () => {
        if (kycCheckInProcess) {
            return (
                <Row indent="adaptive-32-48">
                    <Row flex align="center" indent="8px">
                        <Icon name="clock" size="24px" rightIndent="16px" />
                        <div>{messages.limitsReviewingDocument}</div>
                    </Row>
                    <Button
                        type="link"
                        size="tight"
                        variant="row"
                        caption={messages.viewDetails}
                        negativeMargin
                        onClick={(e) => {
                            e.preventDefault();
                            props.toggleModal(
                                true,
                                <ModalComponent canCloseModal={false}>
                                    <InfoPage
                                        iconName="id-card-search-80"
                                        iconColor="primary"
                                        headerText={messages.thirdAttemptTitle}
                                        secondaryText={<DangerouslyHtml dangerouslyHtml={messages.reviewNewSubtitle} />}
                                        primaryButtonCaption={messages.gotIt}
                                        primaryButtonType="link"
                                        primaryButtonOnClick={() => {
                                            props.toggleModal(false);
                                        }}
                                        insideModalComponent
                                    />
                                </ModalComponent>,
                            );
                        }}
                    />
                </Row>
            );
        } else if (aml_libre_increase_level_link) {
            return (
                <Panel type="reactLink" to={aml_libre_increase_level_link} isHoverable negativeMargin>
                    <DiskBadgeWrapper>
                        <DiskBadge variant="secondary" iconName="arrow-up" />
                    </DiskBadgeWrapper>
                    <Text color="primary">{messages.gaLimitProcessButton}</Text>
                </Panel>
            );
        }
    };

    return (
        <>
            <HeaderPanel title={messages.gaLimitTopUpInfo} indentCompact />
            {libre_topup_limit_6_months && isFirstLvl && (
                <>
                    <Row indent="adaptive-32-48">
                        <Text variant="div" size="header" color={isCurrLessMaxValue(libre_topup_limit_6_months) ? 'primary' : 'error'} indent="4px">
                            {messages.gaLimitTopUpOneOff}
                        </Text>
                        <Text variant="div" size="secondary" color={isCurrLessMaxValue(libre_topup_limit_6_months) ? 'primary' : 'error'}>
                            {resolveArguments(messages.gaLimitTopUpTo, libre_topup_limit_6_months.max_value) + ' '}
                            <span style={{ display: 'none' }}>
                                messages.gaLimitTopUpTo: {messages.gaLimitTopUpTo},<br />
                                libre_topup_limit_6_months.max_value: {libre_topup_limit_6_months.max_value}
                            </span>
                            <Text size="secondary" color={isCurrLessMaxValue(libre_topup_limit_6_months) ? 'primary' : 'error'} smallCaps>
                                {toLowerCase(libre_topup_limit_6_months.cur_iso)}
                            </Text>
                        </Text>
                    </Row>
                    {limitProcessButton()}
                </>
            )}
            {libre_topup_limit_6_months && !isFirstLvl && (
                <>
                    {libre_topup_limit_6_months.unlimited && (
                        <Text variant="div" size="header" color="primary">
                            {messages.gaLimitUnlim}
                        </Text>
                    )}
                    {libre_topup_limit_6_months.active && (
                        <>
                            <Row indent="adaptive-32-48">
                                <LimitBlock
                                    progressWidth={libre_topup_limit_6_months.usedPercent}
                                    isExhaustedLimit={alarm_limits <= libre_topup_limit_6_months.usedPercent}
                                    currency={toLowerCase(libre_topup_limit_6_months.cur_iso)}
                                    spendSum={getLocalizedRawAmount(lang, country, libre_topup_limit_6_months.current_value)}
                                    spendText={messages.profileSpent}
                                    leftSum={getLocalizedRawAmount(lang, country, libre_topup_limit_6_months.leftValue)}
                                    leftText={resolveArguments(messages.gaResetLimitInfo, [
                                        libre_topup_limit_6_months.cycleResetDateDayOfMonth,
                                        messages.libreTopUp6ResetMonth,
                                        libre_topup_limit_6_months.cycleResetDateYear,
                                    ])}
                                />
                            </Row>
                        </>
                    )}
                </>
            )}
            {libre_atm_limit_daily && (
                <>
                    <Separator marginBottom="48px" />
                    <HeaderPanel title={messages.gaAtmLimitInfo} indentCompact />
                    <Row indent="48px">
                        <LimitBlock
                            progressWidth={libre_atm_limit_daily.usedPercent}
                            isExhaustedLimit={alarm_limits <= libre_atm_limit_daily.usedPercent}
                            currency={toLowerCase(libre_atm_limit_daily.cur_iso)}
                            spendSum={getLocalizedRawAmount(lang, country, libre_atm_limit_daily.current_value)}
                            spendText={messages.profileSpent}
                            leftSum={getLocalizedRawAmount(lang, country, libre_atm_limit_daily.leftValue)}
                            leftText={
                                <>
                                    {resolveArguments(messages.gaResetLimitInfo, [
                                        libre_atm_limit_daily.cycleResetDateDayOfMonth,
                                        messages.libreATMDailyResetMonth,
                                        libre_atm_limit_daily.cycleResetDateYear,
                                    ])}
                                </>
                            }
                        />
                    </Row>
                </>
            )}
            {isSuccessKycConfirmation && (
                <>
                    <Separator />
                    <Text variant="div" size="secondary" color="secondary">
                        {messages.kycNotification}
                    </Text>
                </>
            )}
        </>
    );
};

export default connect(null, { toggleModal })(GaLimits);
